import { useCallback, useEffect, useRef, useState } from 'react';

import { useChannelLinkCodeMutation } from '@/features/msg-channels/common/msg-channels.query';
import { type MessagingChannelType } from '@/features/msg-channels/common/msg-channels.types';
import { useIMessageChannelQuery } from '@/features/msg-channels/imessage/imessage.query';
import { useWhatsAppChannelQuery } from '@/features/msg-channels/whatsapp/whatsapp.query';
import { subscriptionApis } from '@/features/subscription/subscription.query';
import { handleExternalUrlClick } from '@/utils/platform';

const CONNECTION_POLL_INTERVAL_MS = 3000;
const PLAN_CHECK_INTERVAL_MS = 2000;
const PLAN_CHECK_ATTEMPTS = 15;

type ChannelConnectionSetupStatus =
  | 'preparing'
  | 'ready'
  | 'waiting'
  | 'expired'
  | 'error';

export const useChannelConnectionState = (
  channelType: MessagingChannelType
) => {
  const imessage = useIMessageChannelQuery({
    enabled: channelType === 'imessage',
  });
  const whatsapp = useWhatsAppChannelQuery({
    enabled: channelType === 'whatsapp_business',
  });
  const connection = channelType === 'imessage' ? imessage : whatsapp;

  return {
    identifier: connection.data?.identifier,
    conversationLink: connection.data?.conversation_link,
    isChecking:
      connection.data === undefined &&
      (connection.isPending || connection.isFetching),
    isError: connection.data === undefined && connection.isError,
    isReady: connection.isSuccess || connection.data !== undefined,
    refetch: connection.refetch,
  };
};

export const useChannelConnectionSetup = ({
  channelType,
  identifier,
  isConnectionReady,
  onConnected,
}: {
  channelType: MessagingChannelType;
  identifier?: string | null;
  isConnectionReady: boolean;
  onConnected: () => void;
}) => {
  const linkCodeMutation = useChannelLinkCodeMutation(channelType);
  const authorizationLaunched = useRef(false);
  const [hasOpenedChannel, setHasOpenedChannel] = useState(false);
  const [isLinkCodeExpired, setIsLinkCodeExpired] = useState(false);
  const isLinkCodeIdle = linkCodeMutation.isIdle;
  const mintLinkCode = linkCodeMutation.mutate;

  useEffect(() => {
    if (!isConnectionReady || identifier || !isLinkCodeIdle) {
      return;
    }
    mintLinkCode();
  }, [identifier, isConnectionReady, isLinkCodeIdle, mintLinkCode]);

  const openChannel = useCallback(() => {
    if (linkCodeMutation.data && !isLinkCodeExpired) {
      authorizationLaunched.current = true;
      setHasOpenedChannel(true);
      handleExternalUrlClick(linkCodeMutation.data.deep_link);
    }
  }, [isLinkCodeExpired, linkCodeMutation.data]);
  const retry = useCallback(() => {
    setHasOpenedChannel(false);
    setIsLinkCodeExpired(false);
    mintLinkCode();
  }, [mintLinkCode]);

  useEffect(() => {
    const linkCode = linkCodeMutation.data;
    if (!linkCode) return;

    setIsLinkCodeExpired(false);
    const timer = setTimeout(
      () => setIsLinkCodeExpired(true),
      linkCode.expires_in_seconds * 1000
    );
    return () => clearTimeout(timer);
  }, [linkCodeMutation.data]);

  useEffect(() => {
    if (!authorizationLaunched.current || !identifier) return;
    authorizationLaunched.current = false;
    onConnected();
  }, [identifier, onConnected]);

  const status: ChannelConnectionSetupStatus = linkCodeMutation.isPending
    ? 'preparing'
    : linkCodeMutation.isError
      ? 'error'
      : isLinkCodeExpired
        ? 'expired'
        : linkCodeMutation.data
          ? hasOpenedChannel
            ? 'waiting'
            : 'ready'
          : 'preparing';

  return {
    status,
    openChannel,
    retry,
  };
};

export const useChannelConnections = (poll = false) => {
  const refetchInterval = poll ? CONNECTION_POLL_INTERVAL_MS : false;

  const imessage = useIMessageChannelQuery({ refetchInterval });
  const whatsapp = useWhatsAppChannelQuery({ refetchInterval });
  const refetchIMessage = imessage.refetch;
  const refetchWhatsApp = whatsapp.refetch;

  useEffect(() => {
    const refetchOnReturn = () => {
      if (document.visibilityState !== 'visible') return;
      void refetchIMessage();
      void refetchWhatsApp();
    };

    document.addEventListener('visibilitychange', refetchOnReturn);
    return () =>
      document.removeEventListener('visibilitychange', refetchOnReturn);
  }, [refetchIMessage, refetchWhatsApp]);

  return {
    connections: {
      imessage,
      whatsapp_business: whatsapp,
    },
  };
};

export const useCheckoutReturn = (hasSubscription: boolean) => {
  const [outcome] = useState(() =>
    new URLSearchParams(window.location.search).get('checkout')
  );
  const [attempts, setAttempts] = useState(0);
  const isPaid = outcome === 'success';
  const isActivating =
    isPaid && !hasSubscription && attempts < PLAN_CHECK_ATTEMPTS;

  useEffect(() => {
    if (!isActivating) return;
    const timer = setTimeout(
      () =>
        void subscriptionApis
          .getStatus()
          .finally(() => setAttempts((count) => count + 1)),
      PLAN_CHECK_INTERVAL_MS
    );
    return () => clearTimeout(timer);
  }, [attempts, isActivating]);

  if (!isPaid) return 'none';
  if (hasSubscription) return 'active';
  return isActivating ? 'activating' : 'delayed';
};
